import type {
    ChatStatusItem,
    PageChangeContentItem,
    PageChangeOperation,
    PageChangeSetStatusItem,
} from '../chat-status';
import {isPageChangeSetStatusItem} from '../chat-status';

type Options = {
    labels: Record<string, string>;
    currentPageId: () => string;
    /** Whether the preview is drawn now. It belongs to the Sidebar, so it shows only while the Sidebar is open. */
    isShown: () => boolean;
    /** Whether the page's buttons must wait, because a reply or another button press is still running. */
    isBlocked: () => boolean;
    /** Takes a change out of the set, the same way Skip in the chat does. */
    skip: (set: PageChangeSetStatusItem, operationId: string) => void;
};

/** A change shown in the preview, with the set it belongs to and that set's place among the previewed sets. */
type PreviewedChange = {
    set: PageChangeSetStatusItem;
    operation: PageChangeOperation;
    order: number;
};

/** Marks every element the preview adds, so it can find and remove them again. */
const PREVIEW_ATTRIBUTE = 'data-ai-preview';
const EDITED_CLASS = 'howspace-ai-preview-edited';
const SHOWING_ORIGINAL_CLASS = 'is-showing-original';
const ORIGINAL_CLASS = 'howspace-ai-preview-original';
const HIGHLIGHT_CLASS = 'is-ai-preview-highlighted';
const DESCRIPTION_PREVIEW_LENGTH = 220;
/** Rows of one set's new containers, numbered from 0. An addition without a placement goes last. */
const MAX_ROW = 9_999;

/**
 * Shows the page the way it will look once the staged changes are applied. Only the browser of the user who staged
 * them shows it, the page itself is not changed.
 *
 * The preview is put together from the real widgets on the page and placeholders, never by rendering a widget
 * from settings that are not saved. An edit shows its new values in place of the widget's own, with the original
 * a click away. An addition is a placeholder drawn where applying the set will put it.
 *
 * The page keeps changing under the preview: live updates replace widgets and containers, and page loads and mode
 * switches replace the whole page. So the preview is laid over the page again whenever the page changes, and every
 * step of that is safe to repeat. A widget someone else updates gets its preview back on top, and one someone
 * deletes takes its preview with it.
 *
 * While a set is being applied, the changes not written yet stay previewed where the user saw them, so the page
 * never looks as if they were dropped. Each one leaves the preview once the real widget has it. A set staged on the
 * page meanwhile is previewed after them.
 *
 * Everything the preview inserts goes through `place()`, so moving real widgets can be added there later without
 * changing how the rest is drawn.
 */
export class PagePreview {
    /** The sets previewed on this page, an applying one first because its additions land first. */
    private sets: PageChangeSetStatusItem[] = [];
    private signature = '';
    private observer: MutationObserver | null = null;
    private frame = 0;
    /** Edits showing the widget's original values instead of the new ones, by preview key. */
    private readonly showingOriginal = new Set<string>();
    /** Additions showing all of their content, by preview key. */
    private readonly expanded = new Set<string>();
    /**
     * A change skipped on the page, and the change next to it. The pressed Skip goes with its change, so focus moves
     * on to the next change once it is gone.
     */
    private refocusAfterSkip: {skipped: string; next: string | null} | null = null;

    constructor(private readonly options: Options) {
    }

    render(statusItems: ChatStatusItem[]): void {
        const pageId = this.options.currentPageId();
        const onPage = this.options.isShown()
            ? statusItems.filter(isPageChangeSetStatusItem).filter(set => set.pageId === pageId)
            : [];
        // Set ids grow over time, so sets being applied stay in the order they were staged.
        this.sets = [
            ...onPage.filter(set => set.status === 'applying').sort((left, right) => left.id.localeCompare(right.id)),
            ...onPage.filter(set => set.status === 'staged'),
        ];

        // Only a staged set's changes are redrawn. An apply only gives its changes outcomes, and sync() takes each
        // one out as it gets it, so the rest of the preview and what the user expanded or focused stays as it is.
        const staged = this.sets.filter(set => set.status === 'staged');
        const signature = JSON.stringify([
            pageId,
            staged.map(set => [set.id, set.revision, set.operations]),
        ]);
        if (signature !== this.signature) {
            this.signature = signature;
            this.withoutObserving(() => this.clear());
        }
        this.sync();
    }

    /**
     * Scrolls to a change on the page and moves focus to it. A change not written yet is found in the preview,
     * or for an edit of a widget open in its editor, the widget itself. An applied one is the widget it added or
     * changed. Returns false when the page does not show it, for example in a hidden or collapsed part of it.
     */
    reveal(set: PageChangeSetStatusItem, operation: PageChangeOperation): boolean {
        const page = this.pageElement();
        if (set.pageId !== this.options.currentPageId() || page === null) {
            return false;
        }

        const previewed = isPreviewed(set, operation);
        const widgetId = previewed ? operation.target : operation.widgetId;
        const element = (previewed ? this.previewElementOf(previewKey(set, operation)) : null)
            ?? (widgetId === null ? null : page.querySelector<HTMLElement>(`#widget-${CSS.escape(widgetId)}`));
        if (!(element instanceof HTMLElement) || !page.contains(element) || element.getClientRects().length === 0) {
            return false;
        }

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        element.scrollIntoView({behavior: reducedMotion ? 'auto' : 'smooth', block: 'center'});
        const focusTarget = element.querySelector<HTMLElement>(`[${PREVIEW_ATTRIBUTE}="bar"]`) ?? element;
        if (!focusTarget.hasAttribute('tabindex')) {
            focusTarget.tabIndex = -1;
            focusTarget.addEventListener('blur', () => focusTarget.removeAttribute('tabindex'), {once: true});
        }
        focusTarget.focus({preventScroll: true});
        element.classList.add(HIGHLIGHT_CLASS);
        window.setTimeout(() => element.classList.remove(HIGHLIGHT_CLASS), 1600);

        return true;
    }

    /**
     * Running replies and button presses come and go without the preview changing, so only the buttons follow them.
     */
    syncDisabled(): void {
        const disabled = this.options.isBlocked();
        document.querySelectorAll<HTMLButtonElement>(`[${PREVIEW_ATTRIBUTE}] button[data-preview-skip]`)
            .forEach(button => {
                button.disabled = disabled;
            });
        this.refocusAfterSkipped();
    }

    /**
     * The Skip pressed on the page goes with its change, which drops focus to the page. Once the request is through
     * and the change is gone, focus moves on to the next change on the page instead.
     */
    private refocusAfterSkipped(): void {
        const refocus = this.refocusAfterSkip;
        if (refocus === null || this.options.isBlocked() || this.skipButtonOf(refocus.skipped) !== null) {
            return;
        }
        this.refocusAfterSkip = null;
        if (refocus.next !== null && (document.activeElement === null || document.activeElement === document.body)) {
            this.skipButtonOf(refocus.next)?.focus({preventScroll: true});
        }
    }

    private skipButtonOf(operationId: string): HTMLButtonElement | null {
        return document.querySelector<HTMLButtonElement>(`[data-preview-skip="${CSS.escape(operationId)}"]`);
    }

    /**
     * The change drawn after this one on the page, or before it when it is the last.
     */
    private neighbourOf(operationId: string): string | null {
        const skips = Array.from(document.querySelectorAll<HTMLButtonElement>('button[data-preview-skip]'));
        const index = skips.findIndex(button => button.dataset.previewSkip === operationId);
        const neighbour = index === -1 ? null : skips[index + 1] ?? skips[index - 1] ?? null;

        return neighbour?.dataset.previewSkip ?? null;
    }

    destroy(): void {
        this.sets = [];
        this.stopObserving();
        this.clear();
    }

    private sync(): void {
        this.withoutObserving(() => {
            const page = this.pageElement();
            if (this.sets.length === 0 || page === null) {
                this.clear();
                return;
            }

            const previewed = this.sets.flatMap((set, order) => set.operations
                .filter(operation => isPreviewed(set, operation))
                .map(operation => ({set, operation, order})));
            this.syncEdits(page, previewed.filter(change => change.operation.op === 'update_item'));
            this.syncAdditions(page, previewed.filter(change => change.operation.op === 'add_widget'));
            this.syncDisabled();
        });

        if (this.sets.length === 0) {
            this.stopObserving();
        } else {
            this.observe();
        }
    }

    private syncEdits(page: HTMLElement, edits: PreviewedChange[]): void {
        const current = new Set<HTMLElement>();
        // A widget shows one edit. The newest one is the one it will end up with.
        [...edits].reverse().forEach(({set, operation: edit}) => {
            if (edit.target === null) {
                return;
            }
            const key = previewKey(set, edit);
            // A widget open for editing shows its own form, never the preview.
            const selector = `.widget[data-widget-id="${CSS.escape(edit.target)}"][data-widget-mode="view"]`;
            page.querySelectorAll<HTMLElement>(selector)
                .forEach(widget => {
                    if (current.has(widget)) {
                        return;
                    }
                    // The copy a live update is about to replace keeps its preview until it is gone.
                    if (widget.dataset.aiPreviewOp !== key) {
                        this.undecorate(widget);
                        this.decorateEdit(widget, set, edit);
                    }
                    widget.classList.toggle(SHOWING_ORIGINAL_CLASS, this.showingOriginal.has(key));
                    this.syncToggle(widget, key);
                    current.add(widget);
                });
        });

        page.querySelectorAll<HTMLElement>(`.${EDITED_CLASS}`).forEach(widget => {
            if (!current.has(widget)) {
                // Only focus on the preview's own controls, which go with it. The widget's own keep theirs.
                const focusedPreview = document.activeElement?.closest(`[${PREVIEW_ATTRIBUTE}]`);
                const hadFocus = focusedPreview instanceof HTMLElement && widget.contains(focusedPreview);
                this.undecorate(widget);
                if (hadFocus) {
                    focusWithoutScrolling(widget);
                }
            }
        });
    }

    private decorateEdit(widget: HTMLElement, set: PageChangeSetStatusItem, edit: PageChangeOperation): void {
        const key = previewKey(set, edit);
        widget.classList.add(EDITED_CLASS);
        widget.dataset.aiPreviewOp = key;

        const bar = this.renderBar(set, edit, this.label('previewEdited'));
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'howspace-ai-preview-toggle';
        toggle.dataset.previewToggle = key;
        toggle.addEventListener('click', () => {
            if (this.showingOriginal.has(key)) {
                this.showingOriginal.delete(key);
            } else {
                this.showingOriginal.add(key);
            }
            this.sync();
        });
        bar.querySelector('.howspace-ai-preview-actions')?.prepend(toggle);
        widget.prepend(bar);

        let anchor: Element = bar;
        const title = edit.fields.title;
        if (title !== undefined) {
            const heading = document.createElement('h2');
            heading.className = 'widget-title widget-padding break-word';
            heading.setAttribute(PREVIEW_ATTRIBUTE, 'value');
            const text = document.createElement('span');
            text.className = 'widget-title-text';
            text.textContent = title;
            heading.append(text);
            anchor = this.replaceInPlace(widget, '.widget-title', heading, anchor);
        }

        const body = edit.fields.text;
        if (body !== undefined) {
            const description = document.createElement('div');
            description.className = 'widget-description widget-padding break-word';
            description.setAttribute(PREVIEW_ATTRIBUTE, 'value');
            // Cleaned on the server by the same sanitizer the widget is saved through.
            description.innerHTML = body;
            this.replaceInPlace(widget, '.widget-description', description, anchor);
        }
    }

    /**
     * Puts the new value right after the original one and marks the original, so either can be shown. A widget
     * without the original element gets the new one after the previous value, or after the bar.
     */
    private replaceInPlace(widget: HTMLElement, selector: string, value: HTMLElement, fallbackAnchor: Element): Element {
        const original = Array.from(widget.querySelectorAll<HTMLElement>(selector))
            .find(element => !element.hasAttribute(PREVIEW_ATTRIBUTE));
        if (original) {
            original.classList.add(ORIGINAL_CLASS);
            original.after(value);
        } else {
            fallbackAnchor.after(value);
        }

        return value;
    }

    private syncToggle(widget: HTMLElement, key: string): void {
        const toggle = widget.querySelector<HTMLButtonElement>(`[data-preview-toggle="${CSS.escape(key)}"]`);
        if (!toggle) {
            return;
        }
        const showingOriginal = this.showingOriginal.has(key);
        toggle.textContent = this.label(showingOriginal ? 'previewShowChange' : 'previewShowOriginal');
        toggle.setAttribute('aria-pressed', String(showingOriginal));
    }

    private undecorate(widget: HTMLElement): void {
        widget.querySelectorAll(`[${PREVIEW_ATTRIBUTE}]`).forEach(element => element.remove());
        widget.querySelectorAll(`.${ORIGINAL_CLASS}`).forEach(element => element.classList.remove(ORIGINAL_CLASS));
        widget.classList.remove(EDITED_CLASS, SHOWING_ORIGINAL_CLASS, HIGHLIGHT_CLASS);
        delete widget.dataset.aiPreviewOp;
    }

    private syncAdditions(page: HTMLElement, additions: PreviewedChange[]): void {
        const wanted = new Set(additions.map(({set, operation}) => previewKey(set, operation)));
        page.querySelectorAll<HTMLElement>('[data-ai-preview-placeholder]').forEach(placeholder => {
            const key = placeholder.dataset.aiPreviewPlaceholder || '';
            if (!wanted.has(key)) {
                const hadFocus = placeholder.contains(document.activeElement);
                placeholder.remove();
                // A placeholder goes when its widget arrives, so focus moves on to the widget.
                const widgetId = hadFocus ? this.operationOf(key)?.widgetId : null;
                const widget = widgetId ? page.querySelector<HTMLElement>(`#widget-${CSS.escape(widgetId)}`) : null;
                if (widget) {
                    focusWithoutScrolling(widget);
                }
            }
        });

        additions.forEach(({set, operation: addition, order}) => {
            const existing = this.previewElementOf(previewKey(set, addition));
            if (existing === null || !page.contains(existing)) {
                existing?.remove();
                this.place(page, this.renderPlaceholder(set, addition), addition, order);
            }
        });

        page.querySelectorAll<HTMLElement>('.howspace-ai-preview-container').forEach(container => {
            if (container.querySelector('[data-ai-preview-placeholder]') === null) {
                container.remove();
            }
        });

        // New rows are drawn at the end of the page in the order they will be added, also after the containers an
        // apply adds meanwhile.
        const parent = page.querySelector<HTMLElement>('.page-containers') ?? page;
        const rows = Array.from(parent.querySelectorAll<HTMLElement>(':scope > .howspace-ai-preview-container'));
        const outOfPlace = rows.some(row => {
            const after = row.nextElementSibling;
            return after !== null && !after.classList.contains('howspace-ai-preview-container');
        });
        if (outOfPlace) {
            rows.sort((left, right) => rowOrder(left) - rowOrder(right)).forEach(row => parent.append(row));
        }
    }

    /**
     * Puts an addition where applying the set will put it: into the empty container at the end of the page, or
     * into a new row drawn after the last container.
     */
    private place(page: HTMLElement, placeholder: HTMLElement, addition: PageChangeOperation, order: number): void {
        const placement = addition.placement ?? {row: MAX_ROW, column: 0, twoColumn: false, containerId: null};

        if (placement.containerId !== null) {
            const column = page.querySelector<HTMLElement>(`#container-${CSS.escape(placement.containerId)} .column`);
            if (column) {
                const buttons = column.querySelector(':scope > .column-buttons');
                if (buttons) {
                    buttons.before(placeholder);
                } else {
                    column.append(placeholder);
                }
                return;
            }
        }

        // Rows of a set staged meanwhile come after the rows of the set being applied.
        const row = order * (MAX_ROW + 1) + Math.min(placement.row, MAX_ROW);
        const columns = this.previewRow(page, row, placement.twoColumn)
            .querySelectorAll<HTMLElement>('.howspace-ai-preview-column');
        (columns[Math.min(placement.column, columns.length - 1)] ?? columns[0])?.append(placeholder);
    }

    private previewRow(page: HTMLElement, row: number, twoColumn: boolean): HTMLElement {
        const existing = page.querySelector<HTMLElement>(`.howspace-ai-preview-container[data-ai-preview-row="${row}"]`);
        if (existing) {
            return existing;
        }

        const container = document.createElement('div');
        container.className = 'howspace-ai-preview-container';
        container.setAttribute(PREVIEW_ATTRIBUTE, 'row');
        container.dataset.aiPreviewRow = String(row);
        const rowElement = document.createElement('div');
        rowElement.className = `howspace-ai-preview-row${twoColumn ? ' is-two-column' : ''}`;
        for (let index = 0; index < (twoColumn ? 2 : 1); index++) {
            const column = document.createElement('div');
            column.className = 'howspace-ai-preview-column';
            rowElement.append(column);
        }
        container.append(rowElement);

        const parent = page.querySelector<HTMLElement>('.page-containers') ?? page;
        const later = Array.from(parent.querySelectorAll<HTMLElement>(':scope > .howspace-ai-preview-container'))
            .find(element => rowOrder(element) > row);
        if (later) {
            later.before(container);
        } else {
            parent.append(container);
        }

        return container;
    }

    private renderPlaceholder(set: PageChangeSetStatusItem, addition: PageChangeOperation): HTMLElement {
        const placeholder = document.createElement('section');
        placeholder.className = 'howspace-ai-preview-placeholder';
        placeholder.setAttribute(PREVIEW_ATTRIBUTE, 'placeholder');
        placeholder.dataset.aiPreviewPlaceholder = previewKey(set, addition);
        placeholder.tabIndex = -1;
        this.fillPlaceholder(placeholder, set, addition);

        return placeholder;
    }

    private fillPlaceholder(placeholder: HTMLElement, set: PageChangeSetStatusItem, addition: PageChangeOperation): void {
        const titleId = `howspace-ai-preview-title-${set.id}-${addition.id}`;
        placeholder.setAttribute('aria-labelledby', titleId);

        const heading = document.createElement('h3');
        heading.className = 'howspace-ai-preview-title';
        const icon = document.createElement('i');
        icon.className = `fa-light fa-${addition.icon || 'square'}`;
        icon.setAttribute('aria-hidden', 'true');
        const title = document.createElement('span');
        title.id = titleId;
        title.textContent = addition.title || this.label('untitled');
        heading.append(icon, title);

        const children: HTMLElement[] = [this.renderBar(set, addition, this.label('previewNew')), heading];
        if (addition.source) {
            const source = document.createElement('p');
            source.className = 'howspace-ai-preview-source';
            source.textContent = this.label('previewSource').replace('%title%', addition.source);
            children.push(source);
        }

        const key = previewKey(set, addition);
        const expanded = this.expanded.has(key);
        const hasMore = addition.items.length > 0 || addition.description.length > DESCRIPTION_PREVIEW_LENGTH;
        if (addition.description) {
            const description = document.createElement('p');
            description.className = 'howspace-ai-preview-description';
            description.textContent = expanded || addition.description.length <= DESCRIPTION_PREVIEW_LENGTH
                ? addition.description
                : `${addition.description.slice(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd()}…`;
            children.push(description);
        }

        const contentId = `howspace-ai-preview-content-${set.id}-${addition.id}`;
        if (expanded && addition.items.length > 0) {
            const items = this.renderItems(addition.items);
            items.id = contentId;
            children.push(items);
        }

        if (hasMore) {
            const toggle = document.createElement('button');
            toggle.type = 'button';
            toggle.className = 'howspace-ai-preview-expand';
            toggle.textContent = this.label(expanded ? 'previewHideContent' : 'previewShowContent');
            toggle.setAttribute('aria-expanded', String(expanded));
            if (expanded && addition.items.length > 0) {
                toggle.setAttribute('aria-controls', contentId);
            }
            toggle.addEventListener('click', () => {
                if (this.expanded.has(key)) {
                    this.expanded.delete(key);
                } else {
                    this.expanded.add(key);
                }
                this.withoutObserving(() => {
                    placeholder.replaceChildren();
                    this.fillPlaceholder(placeholder, set, addition);
                    this.syncDisabled();
                });
                placeholder.querySelector<HTMLElement>('.howspace-ai-preview-expand')?.focus();
            });
            children.push(toggle);
        }

        placeholder.replaceChildren(...children);
    }

    private renderItems(items: PageChangeContentItem[]): HTMLElement {
        const list = document.createElement('ul');
        list.className = 'howspace-ai-preview-items';
        items.forEach(item => {
            const entry = document.createElement('li');
            const text = document.createElement('span');
            text.textContent = item.text;
            entry.append(text);
            if (item.correct) {
                entry.classList.add('is-correct');
                const mark = document.createElement('i');
                mark.className = 'fa-light fa-circle-check';
                mark.setAttribute('aria-hidden', 'true');
                const label = document.createElement('span');
                label.className = 'sr-only';
                label.textContent = this.label('previewCorrect');
                entry.append(' ', mark, label);
            }
            if (item.detail) {
                const detail = document.createElement('p');
                detail.textContent = item.detail;
                entry.append(detail);
            }
            if (item.items.length > 0) {
                entry.append(this.renderItems(item.items));
            }
            list.append(entry);
        });

        return list;
    }

    /**
     * The strip on top of every change on the page: what kind of change it is, and its buttons.
     */
    private renderBar(set: PageChangeSetStatusItem, operation: PageChangeOperation, badgeText: string): HTMLElement {
        const bar = document.createElement('div');
        bar.className = 'howspace-ai-preview-bar';
        bar.setAttribute(PREVIEW_ATTRIBUTE, 'bar');

        const badge = document.createElement('span');
        badge.className = 'howspace-ai-preview-badge';
        badge.textContent = set.status === 'applying' ? this.label('previewApplying') : badgeText;

        const actions = document.createElement('div');
        actions.className = 'howspace-ai-preview-actions';
        bar.append(badge, actions);
        // A change being applied can no longer be skipped.
        if (set.status !== 'staged') {
            return bar;
        }

        const skip = document.createElement('button');
        skip.type = 'button';
        skip.className = 'howspace-ai-preview-skip';
        skip.dataset.previewSkip = operation.id;
        skip.textContent = this.label('skip');
        skip.setAttribute(
            'aria-label',
            this.label('skipNamed').replace('%title%', operation.title || this.label('untitled')),
        );
        skip.disabled = this.options.isBlocked();
        skip.addEventListener('click', () => {
            if (!this.options.isBlocked()) {
                this.refocusAfterSkip = {skipped: operation.id, next: this.neighbourOf(operation.id)};
                this.options.skip(set, operation.id);
            }
        });
        actions.append(skip);

        return bar;
    }

    private operationOf(key: string): PageChangeOperation | null {
        for (const set of this.sets) {
            const operation = set.operations.find(candidate => previewKey(set, candidate) === key);
            if (operation) {
                return operation;
            }
        }

        return null;
    }

    private previewElementOf(key: string): HTMLElement | null {
        const escaped = CSS.escape(key);
        return document.querySelector<HTMLElement>(
            `[data-ai-preview-placeholder="${escaped}"], .${EDITED_CLASS}[data-ai-preview-op="${escaped}"]`,
        );
    }

    private pageElement(): HTMLElement | null {
        const pageId = this.options.currentPageId();
        return pageId === '' ? null : document.querySelector<HTMLElement>(`.page[data-page-id="${CSS.escape(pageId)}"]`);
    }

    private clear(): void {
        document.querySelectorAll<HTMLElement>(`.${EDITED_CLASS}`).forEach(widget => this.undecorate(widget));
        document.querySelectorAll('[data-ai-preview-placeholder], .howspace-ai-preview-container')
            .forEach(element => element.remove());
    }

    /**
     * Watches the page while something is previewed on it, and lays the preview over it again once the page has
     * settled for a frame.
     */
    private observe(): void {
        if (this.observer !== null) {
            return;
        }
        this.observer = new MutationObserver(() => {
            if (this.frame !== 0) {
                return;
            }
            this.frame = window.requestAnimationFrame(() => {
                this.frame = 0;
                this.sync();
            });
        });
        this.connect();
    }

    private connect(): void {
        this.observer?.observe(document.getElementById('main') ?? document.body, {childList: true, subtree: true});
    }

    private stopObserving(): void {
        this.observer?.disconnect();
        this.observer = null;
        if (this.frame !== 0) {
            window.cancelAnimationFrame(this.frame);
            this.frame = 0;
        }
    }

    /**
     * The preview's own changes to the page must not wake the observer, or it would redraw itself forever.
     */
    private withoutObserving(change: () => void): void {
        this.observer?.disconnect();
        try {
            change();
        } finally {
            this.connect();
        }
    }

    private label(key: string): string {
        return this.options.labels[key] || '';
    }
}

/**
 * A change shown in the preview: one waiting for approval, or one an apply has not written yet. An addition names
 * its widget before it exists, and the set says it was added a moment before the widget reaches the page, so it
 * stays previewed until that widget is on the page.
 */
const isPreviewed = (set: PageChangeSetStatusItem, operation: PageChangeOperation): boolean => {
    // New pages and changes to the page itself are not drawn on the page, the card is where they are reviewed.
    if (operation.widgetType === 'page') {
        return false;
    }
    if (operation.status === 'pending') {
        return true;
    }
    const addedOrBeingWritten = operation.status === 'applying'
        || (operation.status === 'applied' && operation.op === 'add_widget');

    return set.status === 'applying'
        && addedOrBeingWritten
        && (operation.widgetId === null || document.getElementById(`widget-${operation.widgetId}`) === null);
};

/** Operation ids are unique only within their set. */
const previewKey = (set: PageChangeSetStatusItem, operation: PageChangeOperation): string => (
    `${set.id}:${operation.id}`
);

const rowOrder = (row: HTMLElement): number => Number(row.dataset.aiPreviewRow);

/** Keeps focus on the page when the preview control that had it goes away. */
const focusWithoutScrolling = (element: HTMLElement): void => {
    if (!element.hasAttribute('tabindex')) {
        element.tabIndex = -1;
        element.addEventListener('blur', () => element.removeAttribute('tabindex'), {once: true});
    }
    element.focus({preventScroll: true});
};
